import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Typography, Card, Button } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useAuth } from '@/contexts/AuthContext';

const { Title, Text } = Typography;

function parseHashParams(hash: string): Record<string, string> {
  const params: Record<string, string> = {};
  const str = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const part of str.split('&')) {
    const [key, ...rest] = part.split('=');
    if (key) params[key] = decodeURIComponent(rest.join('='));
  }
  return params;
}

export default function AuthCallback() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  useEffect(() => {
    // Check for error params in query string (OAuth server errors come as ?error=)
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get('error')) {
      const description = queryParams.get('error_description')?.replace(/\+/g, ' ');
      setOauthError(description || queryParams.get('error')!);
      return;
    }

    // Check for error params in the URL hash (Supabase returns these on OAuth/link failures)
    const hashParams = parseHashParams(window.location.hash);
    if (hashParams.error) {
      const description = hashParams.error_description?.replace(/\+/g, ' ');
      setOauthError(description || hashParams.error);
      return;
    }

    if (isLoading) return;

    if (user) {
      // Check if this is an email confirmation (hash contains type=signup)
      const hash = window.location.hash;
      if (hash.includes('type=signup') || hash.includes('type=email')) {
        setConfirmed(true);
        const timer = setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
        return () => clearTimeout(timer);
      }
      navigate('/dashboard', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  }, [user, isLoading, navigate]);

  if (oauthError) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
        <Card style={{ width: 400, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <WarningOutlined style={{ fontSize: 48, color: 'var(--color-coral)', marginBottom: 16 }} />
          <Title level={4}>Authentication Failed</Title>
          <Text type="secondary">{oauthError}</Text>
          <div style={{ marginTop: 24 }}>
            <Button type="primary" onClick={() => navigate('/login', { replace: true })}>
              Back to Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
        <Card style={{ width: 400, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
          <Title level={4}>Email Confirmed!</Title>
          <Text type="secondary">Your account is active. Redirecting...</Text>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spin size="large" />
    </div>
  );
}
